import React, { useState, useEffect, useCallback } from 'react';
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { toast } from "@/hooks/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import { ArrowLeft, ArrowRight, DollarSign } from 'lucide-react';
import { CardDescription } from "@/components/ui/card";

// Define types for Payroll and Employee data
type Payroll = Database['public']['Tables']['payrolls']['Row'] & {
  employees?: { name: string } | null; // Join with employees table to get employee name
};

interface PayrollManagementTabProps {
  businessId: string | null;
}

// Placeholder for PayrollSettingsForm - will be a separate component
const PayrollSettingsForm: React.FC<{ businessId: string | null }> = ({ businessId }) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">Payroll Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <p>Configure payroll rules here for business ID: {businessId}</p>
        {/* Future implementation: forms for employment types, roles, tax, overtime, frequency */}
      </CardContent>
    </Card>
  );
};

const PayrollManagementTab: React.FC<PayrollManagementTabProps> = ({ businessId }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("payrolls");
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [loadingPayrolls, setLoadingPayrolls] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(new Date());
  const [paymentsEnabled, setPaymentsEnabled] = useState<boolean | null>(null);
  const [enabling, setEnabling] = useState(false);

  // Fetch payment processing status for the business
  useEffect(() => {
    const fetchPaymentStatus = async () => {
      if (!businessId) {
        setPaymentsEnabled(false);
        return;
      }

      try {
        const { data, error } = await supabaseClient
          .from('businesses')
          .select('payment_processing_enabled')
          .eq('id', businessId)
          .single();

        if (error) throw error;
        setPaymentsEnabled(data?.payment_processing_enabled || false);
      } catch (error) {
        console.error('Error fetching payment status:', error);
        setPaymentsEnabled(false);
      }
    };

    fetchPaymentStatus();
  }, [businessId]);

  const handleEnablePayments = async () => {
    if (!businessId) return;

    setEnabling(true);
    try {
      const { error } = await supabaseClient
        .from('businesses')
        .update({ payment_processing_enabled: true })
        .eq('id', businessId);

      if (error) throw error;

      setPaymentsEnabled(true);
      toast.success("Payment system enabled", {
        description: "Payroll management features are now active"
      });
    } catch (error) {
      console.error('Error enabling payments:', error);
      toast.error("Failed to enable payments", {
        description: "Please try again or check your connection"
      });
    } finally {
      setEnabling(false);
    }
  };

  const fetchPayrolls = useCallback(async () => {
    if (!businessId) {
      setPayrolls([]);
      return;
    }

    setLoadingPayrolls(true);
    const periodStart = startOfMonth(currentPeriod);
    const periodEnd = endOfMonth(currentPeriod);

    try {
      const { data, error } = await supabaseClient
        .from('payrolls')
        .select('*, employees(name)') // Select all payroll fields and employee name
        .eq('business_id', businessId)
        .gte('period_start', format(periodStart, 'yyyy-MM-dd'))
        .lte('period_end', format(periodEnd, 'yyyy-MM-dd'))
        .order('period_start', { ascending: false })
        .order('employees.name', { ascending: true });

      if (error) {
        throw error;
      }
      setPayrolls(data || []);
    } catch (error: unknown) {
      console.error("Error fetching payrolls:", error);
      toast.error("Failed to fetch payrolls", { description: error instanceof Error ? error.message : String(error) });
      setPayrolls([]);
    } finally {
      setLoadingPayrolls(false);
    }
  }, [businessId, currentPeriod]);

  useEffect(() => {
    if (paymentsEnabled) {
      fetchPayrolls();
    }
  }, [fetchPayrolls, paymentsEnabled]);

  const handleGeneratePayrolls = async () => {
    if (!businessId) {
      toast.error("Please select a business to generate payrolls.");
      return;
    }
    setLoadingPayrolls(true);
    try {
      const { error } = await supabaseClient.rpc('generate_payrolls_for_period', {
        p_business_id: businessId,
        p_period_start: format(startOfMonth(currentPeriod), 'yyyy-MM-dd'),
        p_period_end: format(endOfMonth(currentPeriod), 'yyyy-MM-dd'),
      });

      if (error) {
        throw error;
      }
      toast.success("Payrolls generated successfully!");
      fetchPayrolls(); // Refresh the list after generation
    } catch (error: unknown) {
      console.error("Error generating payrolls:", error);
      toast.error("Failed to generate payrolls", { description: error instanceof Error ? error.message : String(error) });
    } finally {
      setLoadingPayrolls(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentPeriod(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentPeriod(prev => addMonths(prev, 1));
  };

  const handleApprovePayroll = async (payrollId: string) => {
    if (!businessId) return;

    try {
      const { error } = await supabaseClient.rpc('update_payroll_status', {
        p_payroll_id: payrollId,
        p_new_status: 'approved',
        p_approved_by_user_id: user?.id
      });

      if (error) {
        toast.error("Failed to approve payroll", {
          description: error instanceof Error ? error.message : String(error)
        });
      } else {
        toast.success("Payroll approved!");
        fetchPayrolls();
      }
    } catch (error) {
      toast.error("Failed to approve payroll", {
        description: error instanceof Error ? (error as Error).message : String(error)
      });
    }
  };

  // Show loading while checking payment status
  if (paymentsEnabled === null) {
    return (
      <div className="p-4 space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">Payroll Management</h2>
        <Card>
          <CardContent className="py-8">
            <p className="text-center">Checking payroll settings...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!paymentsEnabled) {
    return (
      <div className="p-4 space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">Payroll Management</h2>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              Payroll Management
            </CardTitle>
            <CardDescription>Manage employee payrolls and generate reports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-foreground">Payment System is Disabled</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  The payment system is currently turned off. Enable payments to access payroll management,
                  generate payrolls, and view payment reports.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="default"
                  onClick={handleEnablePayments}
                  disabled={enabling || !businessId}
                >
                  {enabling ? "Enabling..." : "Enable Payments"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  or configure in Business Settings
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <h2 className="text-3xl font-bold tracking-tight">Payroll Management</h2>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="payrolls">Payrolls</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="payrolls" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
              <CardTitle className="text-sm font-medium">Payrolls for {format(currentPeriod, 'MMMM yyyy')}</CardTitle>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" onClick={handlePreviousMonth}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleNextMonth}>
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button onClick={handleGeneratePayrolls} disabled={loadingPayrolls}>
                  {loadingPayrolls ? "Generating..." : "Generate Payrolls"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPayrolls ? (
                <p className="text-center py-8">Loading payrolls...</p>
              ) : payrolls.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No payrolls found for this period. Generate them above!</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Scheduled Hours</TableHead>
                      <TableHead>Actual Hours</TableHead>
                      <TableHead>Overtime Hours</TableHead>
                      <TableHead>Gross Pay</TableHead>
                      <TableHead>Deductions</TableHead>
                      <TableHead>Net Pay</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrolls.map((payroll) => (
                      <TableRow key={payroll.id}>
                        <TableCell className="font-medium">{payroll.employees?.name || 'N/A'}</TableCell>
                        <TableCell>{format(new Date(payroll.period_start), 'MMM dd')} - {format(new Date(payroll.period_end), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>{payroll.scheduled_hours?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>{payroll.actual_hours?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>{payroll.overtime_hours?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>${payroll.gross_pay.toFixed(2)}</TableCell>
                        <TableCell>${payroll.deductions_amount?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>${payroll.net_pay.toFixed(2)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            payroll.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            payroll.status === 'draft' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}>
                            {payroll.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" disabled={payroll.status !== 'draft'}>Edit</Button>
                          {payroll.status === 'draft' && (
                            <Button
                              variant="default"
                              size="sm"
                              className="ml-2"
                              onClick={() => handleApprovePayroll(payroll.id)}
                            >
                              Approve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <PayrollSettingsForm businessId={businessId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayrollManagementTab;
